import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { AdminSidebar } from '../../components/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Plus, Edit, Trash2, Type, X, Upload, Move, Copy, AlignCenter, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Search } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

import { API_URL, BACKEND_URL } from '../../utils/api';
import { EDITOR_FONTS, loadEditorFonts, loadFontStylesheets, resolveFontUrl } from '../../utils/fonts';
import { textFieldStyle } from '../../utils/invitationStyle';
import { TextContent } from '../../components/design/DesignLayers';
import { fontsForBackground } from '../../components/studio/fontScope';
import { TemplateFieldStyle } from '../../components/admin/TemplateFieldStyle';
import { getErrorMessage } from '../../utils/errors';
import { PerspectivePicker } from '../../components/admin/PerspectivePicker';

const TYPING_SYSTEMS = [
  { value: 'yiddish_keypad', label: 'Yiddish Keypad' },
  { value: 'english_keypad', label: 'English Keypad' },
  { value: 'address_keypad', label: 'Address Keypad' },
  { value: 'email_keypad', label: 'Email Keypad' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'number', label: 'Number (Predefined Options)' },
  { value: 'digits', label: 'Digits' },
  { value: 'digits_4', label: '4 Digits' },
];

const PREVIEW_SCALE = 0.56; // the canvas is 459px wide = 816 * 0.56

// Simplified DraggableText for Admin (No selection ring, just basic drag)
const AdminDraggableText = ({ field, onDrag, canvasRef, isSelected, onSelect }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const elementRef = useRef(null);

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    const rect = elementRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left - rect.width / 2,
      y: e.clientY - rect.top - rect.height / 2
    });
    onSelect(field.id, e);
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const canvasRect = canvas.getBoundingClientRect();
    
    const x = ((e.clientX - canvasRect.left - dragOffset.x) / canvasRect.width) * 100;
    const y = ((e.clientY - canvasRect.top - dragOffset.y) / canvasRect.height) * 100;
    
    onDrag(field.id, Math.max(0, Math.min(100, x)), Math.max(0, Math.min(100, y)));
  }, [isDragging, dragOffset, canvasRef, field.id, onDrag]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div
      ref={elementRef}
      data-text-field="true"
      data-field-id={field.id}
      className={`absolute cursor-move select-none p-2 border-2 transition-all ${
        isSelected ? 'border-gold bg-gold/10 z-50' : 'border-transparent hover:border-gold/30 z-10'
      }`}
      // Drawn like the website editor and the print (letter spacing, outline, shadow, ...). Hidden
      // fields (Simcha Day) still show here so they can be placed.
      style={{ ...textFieldStyle({ ...field, text: field.default_text }, PREVIEW_SCALE), display: undefined }}
      onMouseDown={handleMouseDown}
    >
      <TextContent field={field} text={field.default_text} />
      {isSelected && (
        <div className="absolute -top-6 left-0 bg-gold text-white text-[10px] px-1 rounded whitespace-nowrap" style={{ letterSpacing: 'normal', fontStyle: 'normal', textDecoration: 'none' }}>
          {field.label}
        </div>
      )}
    </div>
  );
};

export default function AdminTemplates() {
  const { user, token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [fonts, setFonts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [categoryFieldInputs, setCategoryFieldInputs] = useState([]);
  const bgFileRef = useRef(null);
  const thumbFileRef = useRef(null);
  const watermarkFileRef = useRef(null);
  const canvasRef = useRef(null);
  const [selectedFieldIds, setSelectedFieldIds] = useState(new Set());
  const [marqueeRect, setMarqueeRect] = useState(null);
  const [isMarqueeActive, setIsMarqueeActive] = useState(false);
  const marqueeStartRef = useRef(null);
  const marqueeRectRef = useRef(null);
  // list search + filters ('all' = no filter)
  const [listSearch, setListSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [partnerFilter, setPartnerFilter] = useState('all'); // 'all' | 'house' | partner id
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'hidden'
  const [viewFilter, setViewFilter] = useState('all'); // 'all' | 'flat' | 'side'

  const [formData, setFormData] = useState({
    name: '', category_id: '', background_image: '', thumbnail_image: '', display_image: '', watermark_image: '',
    price: 9.99, template_code: '', text_fields: [], is_active: true, perspective: null
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (formData.category_id) {
      loadCategoryFieldInputs(formData.category_id);
    } else {
      setCategoryFieldInputs([]);
    }
  }, [formData.category_id, token]);

  // Validate and clean up field_input_id when category or field inputs change
  useEffect(() => {
    if (categoryFieldInputs.length > 0 && formData.text_fields.length > 0) {
      const validFieldInputIds = new Set(categoryFieldInputs.map(fi => fi.id));
      const updatedFields = formData.text_fields.map(field => {
        // If field has a field_input_id that's not in the current category, reset it
        if (field.field_input_id && !validFieldInputIds.has(field.field_input_id)) {
          return { ...field, field_input_id: null };
        }
        return field;
      });
      // Only update if something changed
      const hasChanges = updatedFields.some((field, idx) => 
        field.field_input_id !== formData.text_fields[idx]?.field_input_id
      );
      if (hasChanges) {
        setFormData(prev => {
          const newFields = prev.text_fields.map(field => {
            if (field.field_input_id && !validFieldInputIds.has(field.field_input_id)) {
              return { ...field, field_input_id: null };
            }
            return field;
          });
          return { ...prev, text_fields: newFields };
        });
      }
    }
  }, [categoryFieldInputs, formData.text_fields.length]);

  const loadCategoryFieldInputs = async (categoryId) => {
    try {
      const res = await axios.get(`${API_URL}/admin/categories/${categoryId}/field-inputs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategoryFieldInputs(res.data);
    } catch (error) {
      console.error('Failed to load field inputs:', error);
      setCategoryFieldInputs([]);
    }
  };

  const fetchData = async () => {
    try {
      const [tmplRes, catRes, fontRes] = await Promise.all([
        axios.get(`${API_URL}/admin/templates`), // hidden templates too
        axios.get(`${API_URL}/categories`),
        axios.get(`${API_URL}/fonts`)
      ]);
      setTemplates(tmplRes.data);
      setCategories(catRes.data);
      setFonts(fontRes.data);
      loadEditorFonts(); // the built-in fonts the field picker offers (same list as the website editor)

      // Load fonts
      fontRes.data.forEach(font => {
        if (font.font_url && !document.querySelector(`link[href="${font.font_url}"]`)) {
            const link = document.createElement('link');
            // Handle both absolute URLs (Google Fonts) and relative URLs (custom fonts)
            link.href = resolveFontUrl(font.font_url);
            link.rel = 'stylesheet';
            document.head.appendChild(link);
        }
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file, field) => {
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: JPEG, PNG, WebP, GIF');
      return;
    }

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/upload/image`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const imageUrl = `${BACKEND_URL}${response.data.url}`;
      setFormData(prev => ({ ...prev, [field]: imageUrl }));
      toast.success('Image uploaded');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to upload image'));
    } finally {
      setUploading(false);
    }
  };

  const handleAudioUpload = async (file, fieldIndex) => {
    if (!file) return;
    const allowed = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', 'audio/ogg', 'audio/webm', 'audio/mp4', 'audio/x-m4a'];
    if (!allowed.includes(file.type)) {
      toast.error('Invalid audio type. Allowed: mp3, wav, ogg, webm, m4a');
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const resp = await axios.post(`${API_URL}/upload/audio`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const audioUrl = `${BACKEND_URL}${resp.data.url}`;
      const updated = [...formData.text_fields];
      updated[fieldIndex] = { ...updated[fieldIndex], ivr_prompt_audio_url: audioUrl };
      setFormData({ ...formData, text_fields: updated });
      toast.success('Audio uploaded');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to upload audio'));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Ensure token is set in headers
      const currentToken = token || localStorage.getItem('token');
      if (!currentToken) {
        toast.error('Please log in again');
        window.location.href = '/login';
        return;
      }

      // Clean up text_fields: convert null/empty strings/NaN to null for optional fields
      const cleanedFormData = {
        ...formData,
        text_fields: formData.text_fields.map(field => ({
          ...field,
          line_width: (field.line_width === '' || field.line_width === null || field.line_width === undefined || isNaN(field.line_width)) ? null : parseFloat(field.line_width),
          line_spacing: (field.line_spacing === '' || field.line_spacing === null || field.line_spacing === undefined || isNaN(field.line_spacing)) ? null : parseFloat(field.line_spacing)
        }))
      };
      // A new background makes the smaller on-screen copies (display_image ~1700px,
      // thumbnail_image 900px) stale, so drop them; pages fall back to background_image
      // until new ones are generated.
      if (editingTemplate && cleanedFormData.background_image !== editingTemplate.background_image) {
        cleanedFormData.display_image = '';
        cleanedFormData.thumbnail_image = '';
      }

      // Make request with explicit Authorization header
      const config = {
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        }
      };

      if (editingTemplate) {
        await axios.put(`${API_URL}/admin/templates/${editingTemplate.id}`, cleanedFormData, config);
        toast.success('Template updated');
      } else {
        await axios.post(`${API_URL}/admin/templates`, cleanedFormData, config);
        toast.success('Template created');
      }
      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Template save error:', error);
      if (error.response?.status === 401) {
        toast.error('Your session has expired. Please log in again.');
        window.location.href = '/login';
      } else if (!error.response && (error.code === 'ERR_NETWORK' || error.message === 'Network Error')) {
        toast.error('Network error — the server may be waking up. Please try again in a few seconds.');
      } else {
        const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to save template';
        toast.error(`Failed to save template: ${errorMessage}`);
      }
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      category_id: template.category_id,
      background_image: template.background_image,
      thumbnail_image: template.thumbnail_image || '',
      display_image: template.display_image || '',
      watermark_image: template.watermark_image || '',
      price: template.price || template.digital_price || 9.99, // Fallback
      template_code: template.template_code || '',
      background_code: template.background_code || '',
      text_fields: template.text_fields || [],
      is_active: template.is_active !== false,
      perspective: template.perspective || null
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/admin/templates/${id}`);
      toast.success('Template deleted permanently');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete template'));
    }
  };

  const handleDuplicate = async (template) => {
    try {
      const currentToken = token || localStorage.getItem('token');
      if (!currentToken) {
        toast.error('Please log in again');
        return;
      }
      const res = await axios.post(
        `${API_URL}/admin/templates/${template.id}/duplicate`,
        {},
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      const newTemplate = res.data;
      toast.success(`Template duplicated as "${newTemplate.name}"`);
      await fetchData();
      setEditingTemplate(newTemplate);
      setFormData({
        name: newTemplate.name,
        category_id: newTemplate.category_id,
        background_image: newTemplate.background_image,
        thumbnail_image: newTemplate.thumbnail_image || '',
        display_image: newTemplate.display_image || '',
        watermark_image: newTemplate.watermark_image || '',
        price: newTemplate.price ?? 9.99,
        template_code: newTemplate.template_code || '',
        text_fields: newTemplate.text_fields || [],
        is_active: newTemplate.is_active !== false,
        perspective: newTemplate.perspective || null
      });
      setDialogOpen(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to duplicate template'));
    }
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setFormData({
      name: '', category_id: '', background_image: '', thumbnail_image: '', display_image: '', watermark_image: '',
      price: 9.99, template_code: '', text_fields: [], is_active: true, perspective: null
    });
    setSelectedFieldIds(new Set());
  };

  const handleSelectField = useCallback((id, e) => {
    if (e?.ctrlKey || e?.metaKey) {
      setSelectedFieldIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    } else {
      setSelectedFieldIds(new Set([id]));
    }
  }, []);

  const handleCenterSelected = useCallback(() => {
    const ids = selectedFieldIds;
    if (ids.size === 0) return;
    setFormData(prev => ({
      ...prev,
      text_fields: prev.text_fields.map(tf => ids.has(tf.id) ? { ...tf, x: 50 } : tf)
    }));
  }, [selectedFieldIds]);

  const ARROW_STEP = 1;
  const ARROW_STEP_SHIFT = 5;
  const handleMoveSelected = useCallback((dx, dy) => {
    if (selectedFieldIds.size === 0) return;
    setFormData(prev => ({
      ...prev,
      text_fields: prev.text_fields.map(tf =>
        selectedFieldIds.has(tf.id)
          ? {
              ...tf,
              x: Math.max(0, Math.min(100, tf.x + dx)),
              y: Math.max(0, Math.min(100, tf.y + dy))
            }
          : tf
      )
    }));
  }, [selectedFieldIds]);

  // Arrow key shortcuts for moving text boxes
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedFieldIds.size === 0) return;
      if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') return;
      const step = e.shiftKey ? ARROW_STEP_SHIFT : ARROW_STEP;
      let dx = 0, dy = 0;
      switch (e.key) {
        case 'ArrowUp': dy = -step; break;
        case 'ArrowDown': dy = step; break;
        case 'ArrowLeft': dx = -step; break;
        case 'ArrowRight': dx = step; break;
        default: return;
      }
      if (dx !== 0 || dy !== 0) {
        e.preventDefault();
        handleMoveSelected(dx, dy);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedFieldIds, handleMoveSelected]);

  const addTextField = () => {
    const newField = {
      id: uuidv4(),
      label: 'New Field',
      default_text: 'Text Here',
      x: 50,
      y: 50,
      font_size: 24,
      font_family: 'Playfair Display',
      color: '#1A1A1A',
      text_align: 'center',
      is_required: false,
      max_length: 200,
      line_width: null,  // Optional: max width percentage before wrapping
      line_spacing: null,  // Optional: line height multiplier
      field_input_id: null
    };
    setFormData({ ...formData, text_fields: [...formData.text_fields, newField] });
    setSelectedFieldIds(new Set([newField.id]));
  };

  // Picker fonts, as in the website editor: built-in fonts + uploaded ones allowed on this background.
  const pickerFonts = useMemo(() => {
    const builtIn = EDITOR_FONTS.map((f) => ({ family: f.family, label: f.family, hebrew: f.hebrew, decorative: f.decorative }));
    const known = new Set(builtIn.map((f) => f.family));
    const uploaded = fonts
      .filter((f) => f?.font_family && !known.has(f.font_family) && known.add(f.font_family))
      .map((f) => ({ family: f.font_family, label: f.name || f.font_family, uploaded: true }));
    return fontsForBackground([...builtIn, ...uploaded], editingTemplate);
  }, [fonts, editingTemplate]);

  // A partner template's own fonts aren't in /api/fonts: load them so the picker and canvas use the real font.
  useEffect(() => {
    loadFontStylesheets(editingTemplate?.fonts);
  }, [editingTemplate]);

  const patchTextField = (index, changes) => {
    setFormData((prev) => ({
      ...prev,
      text_fields: prev.text_fields.map((tf, i) => (i === index ? { ...tf, ...changes } : tf)),
    }));
  };

  // A glyph in another font than the selected field: its own fixed field (phone and SMS don't ask for it).
  const addOrnamentField = (ch, family) => {
    const newField = {
      id: uuidv4(),
      label: 'Ornament',
      default_text: ch,
      x: 50,
      y: 50,
      font_size: 72,
      font_family: family,
      color: '#1A1A1A',
      text_align: 'center',
      is_required: false,
      max_length: 200,
      line_width: null,
      line_spacing: null,
      field_input_id: null,
      static: true,
      ornament: true,
    };
    setFormData((prev) => ({ ...prev, text_fields: [...prev.text_fields, newField] }));
    setSelectedFieldIds(new Set([newField.id]));
  };

  const updateTextField = (index, field, value) => {
    if (index < 0 || index >= formData.text_fields.length) {
      console.error('Invalid field index:', index);
      return;
    }
    const updated = [...formData.text_fields];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, text_fields: updated });
  };

  const handleDrag = useCallback((id, x, y) => {
    setFormData(prev => ({
      ...prev,
      text_fields: prev.text_fields.map(tf => tf.id === id ? { ...tf, x, y } : tf)
    }));
  }, []);

  const removeTextField = (index) => {
    const idToRemove = formData.text_fields[index].id;
    const updated = formData.text_fields.filter((_, i) => i !== index);
    setFormData({ ...formData, text_fields: updated });
    setSelectedFieldIds(prev => {
      const next = new Set(prev);
      next.delete(idToRemove);
      return next;
    });
  };

  // the list's own category name also covers deleted categories (/categories is active ones only)
  const templateCategoryName = (t) => categories.find(c => c.id === t.category_id)?.name
    || (t.category_name ? `${t.category_name}${t.category_deleted ? ' (deleted)' : ''}` : 'Unknown');

  const categoryOptions = useMemo(() => {
    const known = new Set(categories.map(c => c.id));
    const extra = new Map();
    templates.forEach(t => {
      if (t.category_id && !known.has(t.category_id) && !extra.has(t.category_id)) {
        extra.set(t.category_id, { id: t.category_id, name: templateCategoryName(t), parent_id: t.category_parent_id || null });
      }
    });
    return [...categories, ...extra.values()];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, templates]);

  const partnerOptions = useMemo(() => {
    const byId = new Map();
    templates.forEach(t => { if (t.partner_id) byId.set(t.partner_id, t.partner_name || 'Unnamed partner'); });
    return [...byId].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [templates]);

  const visibleTemplates = useMemo(() => {
    // a parent category also matches its subcategories
    let catIds = null;
    if (categoryFilter !== 'all') {
      catIds = new Set([categoryFilter]);
      let grew = true;
      while (grew) {
        grew = false;
        categoryOptions.forEach(c => { if (c.parent_id && catIds.has(c.parent_id) && !catIds.has(c.id)) { catIds.add(c.id); grew = true; } });
      }
    }
    const q = listSearch.trim().toLowerCase();
    return templates.filter(t => {
      if (catIds && !catIds.has(t.category_id)) return false;
      if (partnerFilter === 'house' && t.partner_id) return false;
      if (partnerFilter !== 'all' && partnerFilter !== 'house' && t.partner_id !== partnerFilter) return false;
      if (statusFilter === 'active' && t.is_active === false) return false;
      if (statusFilter === 'hidden' && t.is_active !== false) return false;
      if (viewFilter === 'side' && !t.perspective) return false;
      if (viewFilter === 'flat' && t.perspective) return false;
      if (!q) return true;
      return [t.name, t.template_code, t.background_code, templateCategoryName(t), t.partner_name]
        .some(v => String(v || '').toLowerCase().includes(q));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, categories, categoryOptions, listSearch, categoryFilter, partnerFilter, statusFilter, viewFilter]);

  const filtersActive = listSearch.trim() || categoryFilter !== 'all' || partnerFilter !== 'all' || statusFilter !== 'all' || viewFilter !== 'all';
  const clearFilters = () => {
    setListSearch(''); setCategoryFilter('all'); setPartnerFilter('all'); setStatusFilter('all'); setViewFilter('all');
  };

  const firstSelectedId = selectedFieldIds.size > 0 ? [...selectedFieldIds][0] : null;
  const selectedFieldIndex = firstSelectedId ? formData.text_fields.findIndex(f => f.id === firstSelectedId) : -1;
  const selectedField = selectedFieldIndex >= 0 ? formData.text_fields[selectedFieldIndex] : null;

  // On-screen background: the ~1700px display copy, not the 13-23 MB print PNG (slow, and web filters
  // swap files that big for a blank pixel). The display copy is stale once a new background is picked.
  const backgroundChanged = !editingTemplate || formData.background_image !== editingTemplate.background_image;
  const screenBackground = (!backgroundChanged && formData.display_image) || formData.background_image;

  const handleCanvasMouseDown = useCallback((e) => {
    if (e.target.closest('[data-text-field]')) return;
    if (!canvasRef.current) return;
    const cr = canvasRef.current.getBoundingClientRect();
    marqueeStartRef.current = { x: e.clientX, y: e.clientY, canvasLeft: cr.left, canvasTop: cr.top };
    setMarqueeRect({ left: e.clientX - cr.left, top: e.clientY - cr.top, width: 0, height: 0 });
    setIsMarqueeActive(true);
    setSelectedFieldIds(new Set());
  }, []);

  useEffect(() => {
    if (!isMarqueeActive) return;
    const handleMove = (e) => {
      if (!marqueeStartRef.current || !canvasRef.current) return;
      const start = marqueeStartRef.current;
      const cr = canvasRef.current.getBoundingClientRect();
      const startX = start.x - cr.left;
      const startY = start.y - cr.top;
      const endX = e.clientX - cr.left;
      const endY = e.clientY - cr.top;
      const left = Math.min(startX, endX);
      const top = Math.min(startY, endY);
      const width = Math.abs(endX - startX);
      const height = Math.abs(endY - startY);
      const rect = { left, top, width, height, screenLeft: Math.min(start.x, e.clientX), screenTop: Math.min(start.y, e.clientY), screenWidth: width, screenHeight: height };
      marqueeRectRef.current = rect;
      setMarqueeRect({ left, top, width, height });
    };
    const handleUp = () => {
      const rect = marqueeRectRef.current;
      if (rect && rect.width > 4 && rect.height > 4 && canvasRef.current) {
        const cr = canvasRef.current.getBoundingClientRect();
        const ids = formData.text_fields.filter(field => {
          const el = canvasRef.current?.querySelector(`[data-field-id="${field.id}"]`);
          if (!el) return false;
          const elRect = el.getBoundingClientRect();
          const centerX = elRect.left + elRect.width / 2 - cr.left;
          const centerY = elRect.top + elRect.height / 2 - cr.top;
          return centerX >= rect.left && centerX <= rect.left + rect.width &&
                 centerY >= rect.top && centerY <= rect.top + rect.height;
        }).map(f => f.id);
        setSelectedFieldIds(new Set(ids));
      }
      marqueeStartRef.current = null;
      marqueeRectRef.current = null;
      setIsMarqueeActive(false);
      setMarqueeRect(null);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [isMarqueeActive, formData.text_fields]);

  return (
    <div className="min-h-screen bg-paper">
      <AdminSidebar />
      <div className="ml-64 p-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex flex-wrap gap-4 justify-between items-center mb-8">
            <div>
              <h1 className="font-serif text-3xl font-bold text-ink mb-2">Templates</h1>
              <p className="text-ink/60">Manage invitation templates</p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-ink text-paper hover:bg-ink/90" data-testid="add-template-btn">
                  <Plus className="h-4 w-4 mr-2" />Add Template
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-6xl h-[90vh] overflow-hidden flex flex-col p-0">
                <DialogHeader className="p-6 pb-2">
                  <DialogTitle>{editingTemplate ? 'Edit Template' : 'Add Template'}</DialogTitle>
                </DialogHeader>
                
                <div className="flex-1 overflow-hidden flex">
                    {/* Left Sidebar: Controls */}
                    <div className="w-1/3 p-6 pt-0 overflow-y-auto border-r border-border">
                        <form id="template-form" onSubmit={handleSubmit} className="space-y-6">
                            <Tabs defaultValue="basic" className="w-full">
                                <TabsList className="mb-4 w-full">
                                    <TabsTrigger value="basic" className="flex-1">Basic Info</TabsTrigger>
                                    <TabsTrigger value="fields" className="flex-1">Field Properties</TabsTrigger>
                                </TabsList>

                                <TabsContent value="basic" className="space-y-4">
                                    <div>
                                        <Label>Name</Label>
                                        <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="mt-2" />
                                    </div>
                                    <div>
                                        <Label>Category</Label>
                                        <Select value={formData.category_id} onValueChange={(value) => setFormData({ ...formData, category_id: value })}>
                                            <SelectTrigger className="mt-2"><SelectValue placeholder="Select category" /></SelectTrigger>
                                            <SelectContent>
                                                {categories.map((cat) => (<SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>))}
                                            </SelectContent>
                                        </Select>
                                        {formData.category_id && categoryFieldInputs.length > 0 && (
                                            <div className="mt-2 p-3 bg-blue-50 rounded border border-blue-200">
                                                <p className="text-sm font-medium text-blue-900 mb-2">Field Inputs for this category:</p>
                                                <ul className="text-xs text-blue-700 space-y-1">
                                                    {categoryFieldInputs.map((fi, idx) => (
                                                        <li key={fi.id}>
                                                            {idx + 1}. {fi.name} ({TYPING_SYSTEMS.find(ts => ts.value === fi.typing_system)?.label || fi.typing_system})
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <Label>Price ($)</Label>
                                        <Input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })} className="mt-2" />
                                    </div>

                                    <div>
                                        <Label>4-digit Template Code (Catalog)</Label>
                                        <Input
                                            value={formData.template_code}
                                            onChange={(e) => setFormData({ ...formData, template_code: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                                            placeholder="e.g. 1234"
                                            className="mt-2"
                                        />
                                    </div>

                                    <div>
                                        <Label>3-digit Background Number (website only)</Label>
                                        <Input
                                            value={formData.background_code || ''}
                                            onChange={(e) => setFormData({ ...formData, background_code: e.target.value.replace(/\D/g, '').slice(0, 3) })}
                                            placeholder="e.g. 105"
                                            className="mt-2"
                                        />
                                    </div>
                                    
                                    {/* Background Image */}
                                    <div>
                                        <Label>Background Image</Label>
                                        <div className="flex gap-2 mt-2">
                                            <Input 
                                                value={formData.background_image} 
                                                onChange={(e) => setFormData({ ...formData, background_image: e.target.value })} 
                                                placeholder="URL"
                                                className="flex-1" 
                                            />
                                            <input
                                                ref={bgFileRef}
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleImageUpload(e.target.files?.[0], 'background_image')}
                                                className="hidden"
                                            />
                                            <Button type="button" variant="outline" onClick={() => bgFileRef.current?.click()} disabled={uploading}>
                                                <Upload className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Side view (mockup) corners */}
                                    <PerspectivePicker
                                        image={screenBackground}
                                        value={formData.perspective}
                                        onChange={(perspective) => setFormData((prev) => ({ ...prev, perspective }))}
                                        fields={formData.text_fields}
                                    />

                                    {/* Watermark Image */}
                                    <div>
                                        <Label>Watermark Image (Optional)</Label>
                                        <div className="flex gap-2 mt-2">
                                            <Input 
                                                value={formData.watermark_image} 
                                                onChange={(e) => setFormData({ ...formData, watermark_image: e.target.value })} 
                                                placeholder="URL"
                                                className="flex-1" 
                                            />
                                            <input
                                                ref={watermarkFileRef}
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleImageUpload(e.target.files?.[0], 'watermark_image')}
                                                className="hidden"
                                            />
                                            <Button type="button" variant="outline" onClick={() => watermarkFileRef.current?.click()} disabled={uploading}>
                                                <Upload className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </TabsContent>

                                <TabsContent value="fields" className="space-y-4">
                                    <div className="flex justify-between items-center mb-4">
                                        <Label>Text Fields</Label>
                                        <Button type="button" size="sm" variant="outline" onClick={addTextField}>
                                            <Plus className="h-4 w-4 mr-2" /> Add
                                        </Button>
                                    </div>

                                    {selectedFieldIds.size > 0 && (
                                        <div className="flex flex-wrap gap-2 mb-4 p-3 bg-gold/5 border border-gold/20 rounded-lg">
                                            <Button type="button" size="sm" variant="outline" onClick={handleCenterSelected} className="border-gold text-gold hover:bg-gold/10">
                                                <AlignCenter className="h-4 w-4 mr-1" /> Center
                                            </Button>
                                            <div className="flex gap-1 border-l border-border pl-2">
                                                <Button type="button" size="sm" variant="outline" onClick={() => handleMoveSelected(0, -ARROW_STEP)} title="Up (↑)">
                                                    <ArrowUp className="h-4 w-4" />
                                                </Button>
                                                <Button type="button" size="sm" variant="outline" onClick={() => handleMoveSelected(0, ARROW_STEP)} title="Down (↓)">
                                                    <ArrowDown className="h-4 w-4" />
                                                </Button>
                                                <Button type="button" size="sm" variant="outline" onClick={() => handleMoveSelected(-ARROW_STEP, 0)} title="Left (←)">
                                                    <ArrowLeft className="h-4 w-4" />
                                                </Button>
                                                <Button type="button" size="sm" variant="outline" onClick={() => handleMoveSelected(ARROW_STEP, 0)} title="Right (→)">
                                                    <ArrowRight className="h-4 w-4" />
                                                </Button>
                                            </div>
                                            <span className="text-xs text-ink/60 self-center ml-1">
                                                {selectedFieldIds.size} selected · Use arrows or Ctrl+click to multi-select · Drag on canvas to box-select
                                            </span>
                                        </div>
                                    )}
                                    
                                    <div className="space-y-2 mb-6">
                                        {formData.text_fields.map((field, index) => (
                                            <div 
                                                key={field.id}
                                                onClick={(e) => handleSelectField(field.id, e)}
                                                className={`p-3 border rounded cursor-pointer flex justify-between items-center ${selectedFieldIds.has(field.id) ? 'border-gold bg-gold/5' : 'border-border'}`}
                                            >
                                                <span className="font-medium truncate">{field.label}</span>
                                                <Button type="button" size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); removeTextField(index); }} className="text-destructive h-6 w-6 p-0">
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>

                                    {selectedFieldIds.size > 0 && selectedField && selectedFieldIndex >= 0 && selectedFieldIds.size === 1 && (
                                        <div className="border-t pt-4 space-y-4">
                                            <Label className="text-gold">Editing: {selectedField.label || 'Unnamed Field'}</Label>
                                            <div>
                                                <Label>Label</Label>
                                                <Input value={selectedField.label || ''} onChange={(e) => updateTextField(selectedFieldIndex, 'label', e.target.value)} className="mt-1" />
                                            </div>
                                            <div className="border-t pt-4">
                                                <TemplateFieldStyle
                                                    field={selectedField}
                                                    fonts={pickerFonts}
                                                    onPatch={(changes) => patchTextField(selectedFieldIndex, changes)}
                                                    onAddOrnament={addOrnamentField}
                                                />
                                            </div>

                                            {formData.category_id && categoryFieldInputs.length > 0 && (
                                                <div className="border-t pt-4 space-y-3">
                                                    <Label className="text-gold">IVR Field Input Assignment</Label>
                                                    <div>
                                                        <Label>Assign to Field Input</Label>
                                                        <Select 
                                                            value={selectedField?.field_input_id ? String(selectedField.field_input_id) : 'none'} 
                                                            onValueChange={(v) => updateTextField(selectedFieldIndex, 'field_input_id', v === 'none' ? null : v)}
                                                        >
                                                            <SelectTrigger className="mt-1">
                                                                <SelectValue placeholder="Select Field Input (optional)" />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="none">None (not used in IVR)</SelectItem>
                                                                {categoryFieldInputs.map((fi) => (
                                                                    <SelectItem key={fi.id} value={String(fi.id)}>
                                                                        {fi.name} ({TYPING_SYSTEMS.find(ts => ts.value === fi.typing_system)?.label || fi.typing_system})
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                        <p className="text-xs text-ink/60 mt-1">
                                                            Assign this text field to a Field Input to use it in the IVR system. 
                                                            Field Inputs are configured in IVR Management.
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </TabsContent>
                            </Tabs>
                        </form>
                    </div>

                    {/* Right Side: Visual Editor */}
                    <div className="flex-1 bg-stone-100 p-8 flex items-center justify-center overflow-auto relative">
                        <div className="shadow-2xl relative bg-white" style={{
                            width: '459px', // 816 * 0.56 (Scaled down)
                            height: '594px', // 1056 * 0.56
                        }}>
                             <div 
                                ref={canvasRef}
                                className="w-full h-full relative overflow-hidden bg-white cursor-crosshair"
                                style={{
                                    // side view: fields are placed on the flat card, so don't lay them over the tilted photo
                                    backgroundImage: screenBackground && !formData.perspective ? `url(${screenBackground})` : 'none',
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center'
                                }}
                                onMouseDown={handleCanvasMouseDown}
                             >
                                {!formData.background_image && (
                                    <div className="absolute inset-0 flex items-center justify-center text-ink/20 pointer-events-none">
                                        No Background Image
                                    </div>
                                )}
                                {formData.background_image && formData.perspective && (
                                    <div className="absolute top-2 inset-x-2 text-center text-[11px] text-ink/50 pointer-events-none">
                                        Side view: place the fields on this flat card. Basic Info shows them on the photo.
                                    </div>
                                )}
                                
                                {formData.text_fields.map((field) => (
                                    <AdminDraggableText
                                        key={field.id}
                                        field={field}
                                        isSelected={selectedFieldIds.has(field.id)}
                                        onSelect={handleSelectField}
                                        onDrag={handleDrag}
                                        canvasRef={canvasRef}
                                    />
                                ))}

                                {marqueeRect && (
                                    <div
                                        className="absolute border-2 border-gold bg-gold/10 pointer-events-none z-40"
                                        style={{
                                            left: marqueeRect.left,
                                            top: marqueeRect.top,
                                            width: marqueeRect.width,
                                            height: marqueeRect.height
                                        }}
                                    />
                                )}
                             </div>
                        </div>
                        <div className="absolute bottom-4 right-4 bg-white/90 p-2 rounded text-xs text-ink/60">
                            Drag fields to move · Drag on empty area to select multiple · Arrow keys to nudge · Center button for selected
                        </div>
                    </div>
                </div>
                
                <div className="p-4 border-t flex justify-end bg-white">
                    <Button type="submit" form="template-form" className="bg-ink text-paper hover:bg-ink/90">
                        {editingTemplate ? 'Update Template' : 'Create Template'}
                    </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div></div>
          ) : (
            <>
            <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="template-filters">
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink/40" aria-hidden="true" />
                <Input
                  type="search"
                  value={listSearch}
                  onChange={(e) => setListSearch(e.target.value)}
                  placeholder="Search name, code, category, partner"
                  aria-label="Search templates"
                  className="pl-9 bg-white"
                  data-testid="template-search"
                />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-48 bg-white" aria-label="Filter by category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categoryOptions.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.parent_id ? `\u2014 ${cat.name}` : cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={partnerFilter} onValueChange={setPartnerFilter}>
                <SelectTrigger className="w-48 bg-white" aria-label="Filter by designer"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All designers</SelectItem>
                  <SelectItem value="house">Azamein (in-house)</SelectItem>
                  {partnerOptions.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36 bg-white" aria-label="Filter by status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any status</SelectItem>
                  <SelectItem value="active">Live</SelectItem>
                  <SelectItem value="hidden">Hidden</SelectItem>
                </SelectContent>
              </Select>
              <Select value={viewFilter} onValueChange={setViewFilter}>
                <SelectTrigger className="w-44 bg-white" aria-label="Filter by view"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any view</SelectItem>
                  <SelectItem value="flat">Flat</SelectItem>
                  <SelectItem value="side">Side view mockup</SelectItem>
                </SelectContent>
              </Select>
              {filtersActive && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" />Clear
                </Button>
              )}
              <span className="text-sm text-ink/60 ml-auto" aria-live="polite">
                {visibleTemplates.length === templates.length ? `${templates.length} templates` : `${visibleTemplates.length} of ${templates.length} templates`}
              </span>
            </div>
            <div className="bg-white rounded-lg border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Image</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Fields</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleTemplates.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-ink/60 py-10">
                        {templates.length === 0 ? 'No templates yet.' : 'No templates match these filters.'}
                      </TableCell>
                    </TableRow>
                  )}
                  {visibleTemplates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell>
                        <img src={template.thumbnail_image || template.background_image} alt={template.name} className="w-12 h-16 object-cover rounded" />
                      </TableCell>
                      <TableCell className="font-medium">
                        {template.name}
                        <span className="flex flex-wrap gap-1 mt-1">
                          {template.template_code && <span className="text-xs text-ink/50">#{template.template_code}</span>}
                          {template.is_active === false && <span className="text-[11px] px-1.5 rounded bg-ink/10 text-ink/70">Hidden</span>}
                          {template.perspective && <span className="text-[11px] px-1.5 rounded bg-gold/15 text-ink/70">Side view</span>}
                          {template.partner_id && <span className="text-[11px] px-1.5 rounded bg-blue-50 text-blue-800">{template.partner_name || 'Partner'}</span>}
                        </span>
                      </TableCell>
                      <TableCell>{templateCategoryName(template)}</TableCell>
                      <TableCell>${(template.price || template.digital_price || 0).toFixed(2)}</TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1">
                          <Type className="h-4 w-4 text-ink/40" />
                          {template.text_fields?.length || 0}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(template)} title="Edit">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDuplicate(template)} title="Duplicate template (copy fields and IVR mappings)">
                          <Copy className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="ghost" className="text-destructive">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Template?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete the template "{template.name}".
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(template.id)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
